/** @format */

import {styled} from '@mui/material/styles';
import {Box, Typography, Button} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

export const Root = styled(Box)(({theme}) => ({
  margin: theme.spacing(12, 0),
}));

export const HeadBox = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(4),
  marginBottom: theme.spacing(6),
}));

export const HeadTextBox = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

export const HeadingText = styled(Typography)(({theme}) => ({
  color: theme.palette.semantic_dark?.surface.onSurface,
  fontSize: '24px',
  fontWeight: 700,
  lineHeight: '30px',
  letterSpacing: '0.225px',
}));

export const SubHeadingText = styled(Typography)(({theme}) => ({
  color: theme.palette.figma_gray_middle_dark?.main,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 'normal',
}));

export const SearchBox = styled(Box)(({theme}) => ({
  position: 'relative',
  margin: theme.spacing(6, 0),

  '.MuiFormControl-root .MuiInputBase-root': {
    padding: theme.spacing(2.25, 2.25, 2.25, 10.25),

    '.MuiInputBase-input': {
      padding: theme.spacing(1.75, 1, 1.75, 1.25),
    },
  },
}));

export const SearchFieldWrapper = styled(Box)({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  width: '100%',
});

export const StyledSearchIcon = styled(SearchIcon)(({theme}) => ({
  color: theme.palette.semantic_dark?.surface.onSurfaceMuted,
  position: 'absolute',
  left: '16px',
  top: '50%',
  transform: 'translateY(-50%)',
}));

export const ViewModeButtonsBox = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(0.25),
  '& > :first-child button': {
    borderTopLeftRadius: theme.spacing(2),
    borderBottomLeftRadius: theme.spacing(2),
  },

  '& > :last-child': {
    borderTopRightRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
  },
}));

interface ViewModeButtonProps {
  active?: boolean;
}

export const ViewModeButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'active',
})<ViewModeButtonProps>(({theme, active}) => ({
  padding: theme.spacing(4),
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '16px',
  backgroundColor: active
    ? theme.palette.semantic_dark?.primary.activeContainer
    : theme.palette.semantic_dark?.surface.surface1,
  color: active ? theme.palette.semantic_dark?.primary.on : theme.palette.figma_surface?.light,
  borderRadius: 0,
  '& svg path': {
    fill: active ? theme.palette.semantic_dark?.primary.on : theme.palette.figma_surface?.light,
  },
  '&:hover': {
    backgroundColor: active
      ? theme.palette.semantic_dark?.primary.activeContainer
      : theme.palette.semantic_dark?.surface?.surface2,
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.semantic_dark?.disabled.disabledLow,
    color: theme.palette.semantic_dark?.disabled.disabled,
    '& svg path': {
      fill: theme.palette.semantic_dark?.disabled.disabled,
    },
  },
}));

export const CampaignsCount = styled(Typography)(({theme}) => ({
  color: theme.palette.semantic_dark?.surface.onSurfaceLight,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '20px',
}));

export const MediaplanBannerWrapper = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'stretch',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '16px',
}));

export const MediaplanBannerText = styled(Typography)(({theme}) => ({
  color: theme.palette.semantic_dark?.surface.onSurface,
  background: `linear-gradient(90deg, ${theme.palette.semantic_dark?.surface.surface1} 0%, ${theme.palette.semantic_dark?.primary.container} 100%)`,
  padding: theme.spacing(2, 4),
}));

export const MediaplanBannerButton = styled(Button)(({theme}) => ({
  backgroundColor: theme.palette.semantic_dark?.primary.activeContainer,
  color: theme.palette.semantic_dark?.primary.on,
  borderRadius: 0,
  padding: theme.spacing(2, 4),
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: theme.palette.semantic_dark?.primary.hover,
  },
}));

export const FilterButtonsContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: '1px',
  alignItems: 'center',
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.semantic_dark?.surface.surface2}`,
  overflow: 'hidden',
  color: theme.palette.semantic_dark?.surface.onSurfaceMuted,
}));

export const FilterIconWrapper = styled(Box)(({theme}) => ({
  margin: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.semantic_dark?.surface.onSurfaceMuted,
}));
